import { useRef, useState } from 'react';
import type { EventClient } from '@aicores/events';
import type { Prompt } from './types';
import { IconCheck, IconChevronDown, IconCopy, IconThumbDown, IconThumbUp } from './icons';

interface Props {
  prompt: Prompt;
  events: EventClient;
  onCopied?: (promptId: string) => void;
  onViewed?: (promptId: string) => void;
  defaultExpanded?: boolean;
  requiresFoundation?: boolean;
  onRequiresFoundationExpand?: (promptId: string) => void;
}

type Rating = 'up' | 'down' | null;

export function PromptCard({
  prompt,
  events,
  onCopied,
  onViewed,
  defaultExpanded,
  requiresFoundation,
  onRequiresFoundationExpand,
}: Props) {
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
  const [copied, setCopied] = useState(false);
  const [everCopied, setEverCopied] = useState(false);
  const [rating, setRating] = useState<Rating>(null);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const viewFired = useRef(Boolean(defaultExpanded));

  function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && !viewFired.current) {
      viewFired.current = true;
      events.fire('prompt_viewed', {
        prompt_id: prompt.prompt_id,
        prompt_index: prompt.index,
        module_id: prompt.module_id ?? null,
      });
      onViewed?.(prompt.prompt_id);
      if (requiresFoundation) {
        onRequiresFoundationExpand?.(prompt.prompt_id);
      }
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt.prompt_text);
    } catch {
      // Swallow clipboard failures silently; user can still select and copy.
    }
    setCopied(true);
    if (!everCopied) {
      setEverCopied(true);
      onCopied?.(prompt.prompt_id);
    }
    events.fire('prompt_copied', {
      prompt_id: prompt.prompt_id,
      prompt_index: prompt.index,
      module_id: prompt.module_id ?? null,
    });
    window.setTimeout(() => setCopied(false), 1800);
  }

  function rate(value: 'up' | 'down') {
    setRating(value);
    events.fire('rating_submitted', { prompt_id: prompt.prompt_id, rating: value });
  }

  function submitComment() {
    const text = commentText.trim();
    if (!text) return;
    events.fire('comment_submitted', { prompt_id: prompt.prompt_id, comment_text: text });
    setCommentSubmitted(true);
  }

  const classes = ['prompt-card'];
  if (expanded) classes.push('is-expanded');
  if (everCopied) classes.push('is-completed');
  if (prompt.is_bonus) classes.push('is-bonus-badge');

  return (
    <article
      id={`prompt-${prompt.prompt_id}`}
      className={classes.join(' ')}
      data-prompt-id={prompt.prompt_id}
    >
      <button type="button" className="prompt-header" onClick={toggle} aria-expanded={expanded}>
        <div className="prompt-header-left">
          <div className="prompt-index">{String(prompt.index).padStart(2, '0')}</div>
          <div className="prompt-title">{prompt.title}</div>
          {prompt.is_bonus && prompt.bonus_label ? (
            <span className="prompt-bonus-badge">Bonus</span>
          ) : null}
          <IconCheck className="prompt-status-icon" aria-hidden="true" />
        </div>
        <div className="prompt-expand" aria-hidden="true">
          <IconChevronDown />
        </div>
      </button>
      <div className="prompt-body">
        <div className="prompt-body-inner">
          <div className="prompt-meta">
            <div className="prompt-meta-item">
              <div className="prompt-meta-label">What it does</div>
              <div className="prompt-meta-value">{prompt.what_it_does}</div>
            </div>
            {prompt.use_when ? (
              <div className="prompt-meta-item">
                <div className="prompt-meta-label">Use when</div>
                <div className="prompt-meta-value">{prompt.use_when}</div>
              </div>
            ) : null}
            {typeof prompt.time_minutes === 'number' ? (
              <div className="prompt-meta-item">
                <div className="prompt-meta-label">Time</div>
                <div className="prompt-meta-value">{prompt.time_minutes} minutes</div>
              </div>
            ) : null}
            {prompt.best_with ? (
              <div className="prompt-meta-item">
                <div className="prompt-meta-label">Best with</div>
                <div className="prompt-meta-value">{prompt.best_with}</div>
              </div>
            ) : null}
            {prompt.note ? (
              <div className="prompt-meta-item">
                <div className="prompt-meta-label">Note</div>
                <div className="prompt-meta-value">{prompt.note}</div>
              </div>
            ) : null}
          </div>

          <div className="prompt-text-container">
            <div className="prompt-text-label">
              <div className="prompt-text-label-text">The Prompt</div>
            </div>
            <PromptBody text={prompt.prompt_text} />
          </div>

          <div className="prompt-actions">
            <button type="button" className="btn btn-primary" onClick={copy}>
              {copied ? (
                <>
                  <IconCheck aria-hidden="true" /> Copied
                </>
              ) : (
                <>
                  <IconCopy aria-hidden="true" /> Copy prompt
                </>
              )}
            </button>
            <div className="rating-group">
              <span className="rating-label">Was this helpful?</span>
              <button
                type="button"
                className={`rating-btn${rating === 'up' ? ' is-active-up' : ''}`}
                aria-label="Helpful"
                aria-pressed={rating === 'up'}
                onClick={() => rate('up')}
              >
                <IconThumbUp />
              </button>
              <button
                type="button"
                className={`rating-btn${rating === 'down' ? ' is-active-down' : ''}`}
                aria-label="Not helpful"
                aria-pressed={rating === 'down'}
                onClick={() => rate('down')}
              >
                <IconThumbDown />
              </button>
            </div>
          </div>

          {rating === 'down' && !commentSubmitted ? (
            <div className="comment-box">
              <label className="comment-box-label" htmlFor={`comment-${prompt.prompt_id}`}>
                What didn't land? (optional — helps us sharpen this prompt.)
              </label>
              <textarea
                id={`comment-${prompt.prompt_id}`}
                className="comment-box-textarea"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <div className="comment-box-actions">
                <button type="button" className="btn btn-primary" onClick={submitComment}>
                  Send
                </button>
              </div>
            </div>
          ) : null}
          {commentSubmitted ? (
            <div className="comment-box-submitted">Thanks — noted.</div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function PromptBody({ text }: { text: string }) {
  const parts: Array<{ kind: 'text' | 'bracket'; value: string }> = [];
  const re = /\[([^\]]+)\]/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ kind: 'text', value: text.slice(last, match.index) });
    }
    parts.push({ kind: 'bracket', value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', value: text.slice(last) });

  return (
    <div className="prompt-text">
      {parts.map((p, i) =>
        p.kind === 'bracket' ? (
          <span key={i} className="bracket">
            {p.value}
          </span>
        ) : (
          <span key={i}>{p.value}</span>
        ),
      )}
    </div>
  );
}
